import { formatDate } from '@angular/common';
import {Observable, forkJoin, of} from 'rxjs';
import {MatDialog} from '@angular/material/dialog';
import {ObjectPrintTemplatesDialogComponent, PrintDialogResult} from '@views/secured/beyond-core/object-print-templates-dialog/object-print-templates-dialog.component';
import {take, exhaustMap, filter, map} from 'rxjs/operators';

export function saveArrayBufferAsFile(result: ArrayBuffer, fileName: string): void {
  const url = URL.createObjectURL(new Blob([result]));
  saveToDisk(url, fileName);
}

export function saveBlobAsFile(blob: Blob, fileName: string): void {
  const url = window.URL.createObjectURL(blob);
  saveToDisk(url, fileName);
}

export function saveStringAsFile(content: string, type: string, fileName: string): void {
  const blob = new Blob([content], {type});
  const url = window.URL.createObjectURL(blob);
  saveToDisk(url, fileName);
}

function saveToDisk(url: string, fileName: string): void {
  const file = document.createElement('a');
  file.href = url;
  file.download = fileName;
  file.click();
  URL.revokeObjectURL(url);
}

export function generateFileName(objectName: string, extension: string): string {
  return objectName + '_' + formatDate(new Date(), 'dd_MM_yyyy_HH_mm_ss', 'en_US') + extension;
}

export function openFileInNewWindow(val: Blob): void {
  const fileUrl = URL.createObjectURL(val);
  window.open(fileUrl);
}

export function downloadFile(blob: Blob, fileName: string): void {
  const url = window.URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;

  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export function downloadBlob(content: any, type: string, name: string): void {
  const data = new Blob([content], {type: type});
  const fileUrl = window.URL.createObjectURL(data);

  // Create a link element
  const link = document.createElement('a');

  // Set link's href to point to the Blob URL
  link.href = fileUrl;
  link.download = name;

  // Append link to the body
  document.body.appendChild(link);

  // Dispatch click event on the link
  // This is necessary as link.click() does not work on the latest firefox
  link.dispatchEvent(
    new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      view: window,
    })
  );

  // Remove link from body
  document.body.removeChild(link);
}

export function base64ToByteArray(base64: string): Uint8Array {
  const binaryString = window.atob(base64);
  const byteArray = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    byteArray[i] = binaryString.charCodeAt(i);
  }
  return byteArray;
}

export function blobToBase64Observable(blob: Blob): Observable<string> {
  return new Observable<string>(observer => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      // Ukloni prefix "data:*/*;base64,"
      const base64Index = result.indexOf(',') + 1;
      const base64String = result.substring(base64Index);
      observer.next(base64String);
      observer.complete();
    };
    reader.onerror = err => observer.error(err);
    reader.readAsDataURL(blob);
  });
}

export function blobsToBase64Observable(blobs: Blob[]): Observable<string[]> {
  if (!blobs || blobs.length === 0) {
    return of([]);
  }

  const base64Observables = blobs.map(blob => blobToBase64Observable(blob));
  return forkJoin(base64Observables);
}

export function getBase64FromPrintDialog(
  matDialog: MatDialog,
  printDataFn: () => Observable<any>,
  objectDefinitionName: string
): Observable<{ base64String: string | null }> {
  return new Observable(observer => {
    printDataFn()
      .pipe(
        take(1),
        exhaustMap(val =>
          ObjectPrintTemplatesDialogComponent.open(matDialog, {
            objectName: objectDefinitionName,
            type: val.type,
            params: val.params,
            printData: val.printData,
            fileName: val.fileName,
            skipAutoDownload: true,
          }).afterClosed().pipe(
            filter((result): result is PrintDialogResult => !!result && 'blobs' in result && result.blobs.length > 0),
            map(result => ({ printData: val.printData, blob: result.blobs[0] }))
          )
        ),
        exhaustMap(({ blob }) => blobToBase64Observable(blob)),
        map(base64String => ({ base64String }))
      )
      .subscribe({
        next: val => {
          observer.next(val);
          observer.complete();
        },
        error: err => observer.error(err),
      });
  });
}

export function getBase64ArrayFromPrintDialog(
  matDialog: MatDialog,
  printDataFn: () => Observable<any>,
  objectDefinitionName: string
): Observable<{ base64Strings: string[] | null }> {
  return new Observable(observer => {
    printDataFn()
      .pipe(
        take(1),
        exhaustMap(val =>
          ObjectPrintTemplatesDialogComponent.open(matDialog, {
            objectName: objectDefinitionName,
            type: val.type,
            params: val.params,
            printData: val.printData,
            fileName: val.fileName,
            skipAutoDownload: true,
          }).afterClosed().pipe(
            filter((result): result is PrintDialogResult => !!result && 'blobs' in result),
            map(result => ({
              printData: val.printData,
              blobs: result.blobs as Blob[]
            }))
          )
        ),
        exhaustMap(({ blobs }) => blobsToBase64Observable(blobs)),
        map(base64Strings => ({ base64Strings }))
      )
      .subscribe({
        next: val => {
          observer.next(val);
          observer.complete();
        },
        error: err => observer.error(err),
      });
  });
}
